import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
} from "react";
import { Form, Button, Row, Col, Modal, Container } from "react-bootstrap";
import axios from "axios";
import "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react"; // React Data Grid Component
import { useSelector } from "react-redux";
import {
  agGridOptionsSingle,
  agGridTheme,
  HOST_URL,
  LEGACY_MARKUP_CUTOFF,
} from "../../helpers/constants/Constants";
import Comments from "../comments/Comments";
import LoadingScreen from "../loadingScreen/LoadingScreen";

// Markups created before this date were made in the older mView tool.
const LEGACY_MARKUP_CUTOFF_TIMESTAMP = Date.parse(LEGACY_MARKUP_CUTOFF);

const MarkupsInfo = () => {
  const { selectedProject, userDetails } = useSelector(
    (state) => state.globalReducer,
  );
  const userData = useSelector((state) => state.userData.data);
  const [rowData, setRowData] = useState([]);
  const [gridApi, setGridApi] = useState();
  const [infoModal, setInfoModal] = useState(false);
  const [selectedMarkup, setSelectedMarkup] = useState(null);
  const [gridPageSizeMode, setGridPageSizeMode] = useState("auto");
  const [markupsLoading, setMarkupsLoading] = useState(true);
  const [markupInfoLoading, setMarkupInfoLoading] = useState(false);
  const [markupInfoSaving, setMarkupInfoSaving] = useState(false);
  const [selectedMarkupImage, setSelectedMarkupImage] = useState(null);
  const [selectedMarkupImageLoading, setSelectedMarkupImageLoading] =
    useState(false);

  const getProjectId = useCallback(() => {
    const projectId = selectedProject?.[0]?.id;
    if (!projectId) {
      return "";
    }
    const projectIdParts = String(projectId).split("/");
    return projectIdParts[projectIdParts.length - 1] || "";
  }, [selectedProject]);

  const onCloseInfoModal = () => {
    if (markupInfoSaving) {
      return;
    }
    setInfoModal(false);
    setMarkupInfoLoading(false);
    setSelectedMarkupImage(null);
    setSelectedMarkupImageLoading(false);
  };

  const getMarkupStatus = useCallback((markup) => {
    const rawStatus = String(markup?.status || "").trim().toLowerCase();

    if (!rawStatus) {
      //check markup.actioned and markup.approved
      if (markup?.actioned) {
        return "Closed";
      }
      if (markup?.approved) {
        return "Under review";
      }
      return "Open";
    }

    if (rawStatus === "closed") {
      return "Closed";
    }

    if (
      rawStatus === "under review" ||
      rawStatus === "under_review" ||
      rawStatus === "under-review" ||
      rawStatus === "underreview"
    ) {
      return "Under review";
    }

    return "Open";
  }, []);

  const getMarkupStatusDisplay = useCallback(
    (markup) => {
      const statusLabel = getMarkupStatus(markup);
      const palette =
        statusLabel === "Closed"
          ? { head: "#43a047", stem: "#1b5e20", label: "Closed" }
          : statusLabel === "Under review"
            ? { head: "#ffb300", stem: "#f9a825", label: "Under review" }
            : { head: "#e53935", stem: "#b71c1c", label: "Open" };

      return {
        label: palette.label,
        badgeStyle: {
          color: "#ffffff",
          background: `linear-gradient(135deg, ${palette.head}, ${palette.stem})`,
          border: "1px solid rgba(255, 255, 255, 0.22)",
        },
      };
    },
    [getMarkupStatus],
  );

  const getMarkupGroupStatus = useCallback(
    (markup) => {
      if (markup?.archived) {
        return "Archived";
      }

      return getMarkupStatus(markup);
    },
    [getMarkupStatus],
  );

  const compareMarkupGroupStatus = useCallback((left, right) => {
    const orderMap = {
      Archived: 0,
      Open: 1,
      "Under review": 2,
      Closed: 3,
    };

    const leftRank =
      orderMap[String(left || "").trim()] ?? Number.MAX_SAFE_INTEGER;
    const rightRank =
      orderMap[String(right || "").trim()] ?? Number.MAX_SAFE_INTEGER;

    if (leftRank !== rightRank) {
      return leftRank - rightRank;
    }

    return String(left || "").localeCompare(String(right || ""));
  }, []);

  const formatDate = useCallback((value) => {
    if (!value) {
      return "";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "";
    }
    return date.toLocaleDateString("en-GB");
  }, []);

  const normalizeCreatedFor = useCallback((createdFor) => {
    if (!createdFor) {
      return [];
    }
    if (Array.isArray(createdFor)) {
      return createdFor
        .map((item) => {
          if (typeof item === "string") {
            return item;
          }
          if (item && typeof item === "object") {
            return item.email || item.value || item.label || "";
          }
          return "";
        })
        .map((value) => String(value || "").trim())
        .filter(Boolean);
    }
    if (typeof createdFor === "string") {
      const trimmed = createdFor.trim();
      if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) {
            return parsed
              .map((item) => {
                if (typeof item === "string") {
                  return item;
                }
                if (item && typeof item === "object") {
                  return item.email || item.value || item.label || "";
                }
                return "";
              })
              .map((value) => String(value || "").trim())
              .filter(Boolean);
          }
        } catch (err) {
          // Fall back to comma-separated parsing.
        }
      }

      return createdFor
        .split(",")
        .map((value) => value.trim())
        .filter(Boolean);
    }
    return [];
  }, []);

  const decodeMarkupBuffer = useCallback((bytes) => {
    if (!bytes || !bytes.length) {
      return "";
    }

    try {
      return new TextDecoder().decode(new Uint8Array(bytes));
    } catch (err) {
      let text = "";
      for (let i = 0; i < bytes.length; i += 1) {
        text += String.fromCharCode(bytes[i]);
      }
      return text;
    }
  }, []);

  const decodePayloadCandidate = useCallback(
    (candidate) => {
      if (!candidate) {
        return null;
      }

      if (typeof candidate === "string") {
        try {
          return JSON.parse(candidate);
        } catch (err) {
          return null;
        }
      }

      if (candidate?.type === "Buffer" && Array.isArray(candidate?.data)) {
        const decoded = decodeMarkupBuffer(candidate.data);
        try {
          return JSON.parse(decoded);
        } catch (err) {
          return null;
        }
      }

      if (Array.isArray(candidate)) {
        // Normal API responses are often arrays of objects; keep those as-is.
        const isByteArray = candidate.every(
          (value) => typeof value === "number" && Number.isFinite(value),
        );
        if (!isByteArray) {
          return candidate;
        }

        const decoded = decodeMarkupBuffer(candidate);
        try {
          return JSON.parse(decoded);
        } catch (err) {
          return null;
        }
      }

      if (candidate instanceof ArrayBuffer) {
        const decoded = decodeMarkupBuffer(Array.from(new Uint8Array(candidate)));
        try {
          return JSON.parse(decoded);
        } catch (err) {
          return null;
        }
      }

      if (ArrayBuffer.isView(candidate)) {
        const decoded = decodeMarkupBuffer(Array.from(candidate));
        try {
          return JSON.parse(decoded);
        } catch (err) {
          return null;
        }
      }

      if (typeof candidate === "object" && !Array.isArray(candidate)) {
        return candidate;
      }

      return null;
    },
    [decodeMarkupBuffer],
  );

  const normalizeMarkupsPayload = useCallback(
    (response) => {
      const payload = decodePayloadCandidate(response?.data);

      if (Array.isArray(payload)) {
        return payload;
      }

      if (Array.isArray(payload?.data)) {
        return payload.data;
      }

      const nested = decodePayloadCandidate(payload?.data);
      if (Array.isArray(nested)) {
        return nested;
      }

      return [];
    },
    [decodePayloadCandidate],
  );

  const getMarkupCreatedBy = useCallback((markup) => {
    const candidate =
      markup?.userName ||
      markup?.createdBy ||
      markup?.createdByName ||
      markup?.user?.name ||
      markup?.user?.email ||
      markup?.createdByEmail ||
      "";

    return String(candidate).trim();
  }, []);

  const getMarkupCreatedOn = useCallback((markup) => {
    return (
      markup?.created ||
      markup?.createdDate ||
      markup?.createdOn ||
      markup?.dateCreated ||
      markup?.created_at ||
      ""
    );
  }, []);

  const getMarkupCreatedFor = useCallback((markup) => {
    return (
      markup?.createdFor ??
      markup?.assignedTo ??
      markup?.created_for ??
      markup?.createdForUsers ??
      markup?.assignedUsers ??
      []
    );
  }, []);

  const normalizeModelReferenceValue = useCallback((value) => {
    if (value == null) {
      return "";
    }

    return String(value)
      .trim()
      .replace(/^urn:/i, "")
      .toLowerCase();
  }, []);

  const getMarkupDocumentName = useCallback(
    (markup) => {
      if (!markup || typeof markup !== "object") {
        return "";
      }

      const parsedMarkupState = decodePayloadCandidate(markup?.markupState);
      const markupState =
        parsedMarkupState && typeof parsedMarkupState === "object"
          ? parsedMarkupState
          : markup?.markupState && typeof markup.markupState === "object"
            ? markup.markupState
            : null;

      const normalizedSeedUrn = normalizeModelReferenceValue(
        markupState?.seedURN ||
          markupState?.seedUrn ||
          markupState?.seedurn ||
          "",
      );

      const parsedSelectedModels = decodePayloadCandidate(markup?.selectedModels);
      const selectedModels = Array.isArray(parsedSelectedModels)
        ? parsedSelectedModels
        : Array.isArray(markup?.selectedModels)
          ? markup.selectedModels
          : [];

      const matchedModel = normalizedSeedUrn
        ? selectedModels.find((model) => {
            const normalizedModelRefCandidates = [
              model?.id,
              model?.urn,
              model?.modelGuid,
              model?.guid,
            ].map((candidate) => normalizeModelReferenceValue(candidate));

            return normalizedModelRefCandidates.some(
              (candidate) => candidate && candidate === normalizedSeedUrn,
            );
          })
        : null;

      const fallbackModel =
        matchedModel ||
        (selectedModels.length === 1 ? selectedModels[0] : null) ||
        null;

      return String(
        fallbackModel?.documentName ||
          fallbackModel?.name ||
          fallbackModel?.modelName ||
          "",
      ).trim();
    },
    [decodePayloadCandidate, normalizeModelReferenceValue],
  );

  const normalizeMarkupRecord = useCallback(
    (record) => {
      if (!record || typeof record !== "object") {
        return record;
      }

      return {
        ...record,
        title: record.title || record.markupTitle || record.name || "",
        category: record.category || "",
        asset: record.asset || record.processArea || "",
        processArea: record.processArea || record.asset || "",
        userName: getMarkupCreatedBy(record),
        created: getMarkupCreatedOn(record),
        createdFor: getMarkupCreatedFor(record),
        documentName: getMarkupDocumentName(record),
      };
    },
    [
      getMarkupCreatedBy,
      getMarkupCreatedFor,
      getMarkupCreatedOn,
      getMarkupDocumentName,
    ],
  );

  useEffect(() => {
    const fetchProjectMarkups = async () => {
      setMarkupsLoading(true);
      const projectId = getProjectId();
      if (!projectId) {
        setRowData([]);
        setMarkupsLoading(false);
        return;
      }

      try {
        const response = await axios({
          method: "get",
          url: HOST_URL + "/markups/getMarkups",
          params: { projectId },
          withCredentials: true,
        });
        const markups = normalizeMarkupsPayload(response).map((item) =>
          normalizeMarkupRecord(item),
        );
        setRowData(markups);
      } catch (err) {
        setRowData([]);
      } finally {
        setMarkupsLoading(false);
      }
    };

    fetchProjectMarkups();
  }, [getProjectId, normalizeMarkupRecord, normalizeMarkupsPayload]);

  const parseMarkupData = useCallback(
    (rawData) => {
      if (!rawData) {
        return null;
      }

      if (typeof rawData === "string") {
        try {
          return JSON.parse(rawData);
        } catch (err) {
          return null;
        }
      }

      if (rawData?.type === "Buffer" && Array.isArray(rawData?.data)) {
        const decoded = decodeMarkupBuffer(rawData.data);
        try {
          return JSON.parse(decoded);
        } catch (err) {
          return null;
        }
      }

      if (Array.isArray(rawData)) {
        const decoded = decodeMarkupBuffer(rawData);
        try {
          return JSON.parse(decoded);
        } catch (err) {
          return null;
        }
      }

      if (typeof rawData === "object") {
        return rawData;
      }

      return null;
    },
    [decodeMarkupBuffer],
  );

  const normalizeImageSource = useCallback((source) => {
    const value = String(source || "").trim();
    if (!value) {
      return null;
    }

    if (value.startsWith("blob:") || /^https?:\/\//i.test(value)) {
      return value;
    }

    if (!value.startsWith("data:")) {
      return null;
    }

    if (value.startsWith("data:image/")) {
      return value;
    }

    try {
      const decoded = atob(value.split(",")[1] || "");
      if (decoded.startsWith("data:image/")) {
        return decoded;
      }
    } catch (err) {
      return null;
    }

    return null;
  }, []);

  const resolvePersistedMarkupData = useCallback(
    (markup) => {
      if (!markup || typeof markup !== "object") {
        return null;
      }

      return (
        parseMarkupData(markup?.data) ||
        parseMarkupData(markup?.markupData) ||
        (markup?.data && typeof markup.data === "object" ? markup.data : null) ||
        (markup?.markupData && typeof markup.markupData === "object"
          ? markup.markupData
          : null)
      );
    },
    [parseMarkupData],
  );

  const normalizeLegacyImageDataUrl = useCallback(
    (rawValue) => {
      if (!rawValue) {
        return null;
      }

      if (typeof rawValue === "object") {
        const getRawBytes = (value) => {
          if (value instanceof ArrayBuffer) {
            return Array.from(new Uint8Array(value));
          }
          if (ArrayBuffer.isView(value)) {
            return Array.from(
              new Uint8Array(value.buffer, value.byteOffset, value.byteLength),
            );
          }
          if (Array.isArray(value)) {
            return value;
          }
          if (value?.type === "Buffer") {
            return getRawBytes(value.data);
          }
          if (
            value &&
            typeof value === "object" &&
            Object.keys(value).some((key) => /^\d+$/.test(key))
          ) {
            return Object.keys(value)
              .filter((key) => /^\d+$/.test(key))
              .sort((left, right) => Number(left) - Number(right))
              .map((key) => value[key]);
          }
          return null;
        };
        const rawBytes = getRawBytes(rawValue);

        if (!rawBytes?.length) {
          return null;
        }

        const bytes = Uint8Array.from(rawBytes);
        const decoded = decodeMarkupBuffer(bytes).trim();
        if (decoded.startsWith("data:")) {
          return normalizeLegacyImageDataUrl(decoded);
        }

        let mimeType = null;
        if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e) {
          mimeType = "image/png";
        } else if (bytes[0] === 0xff && bytes[1] === 0xd8) {
          mimeType = "image/jpeg";
        } else if (bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46) {
          mimeType = "image/gif";
        }

        if (mimeType) {
          let binary = "";
          const chunkSize = 0x8000;
          for (let index = 0; index < bytes.length; index += chunkSize) {
            binary += String.fromCharCode(
              ...bytes.subarray(index, Math.min(index + chunkSize, bytes.length)),
            );
          }
          return `data:${mimeType};base64,${btoa(binary)}`;
        }

        return null;
      }

      return normalizeImageSource(rawValue);
    },
    [decodeMarkupBuffer, normalizeImageSource],
  );

  const fetchLegacyMarkupPreview = useCallback(async (markupId) => {
    if (!markupId) {
      return null;
    }

    try {
      const previewResponse = await axios.get(
        `${HOST_URL}/markups/getMarkupPreview/${markupId}`,
        { responseType: "text", withCredentials: true },
      );
      let rawPreview = previewResponse.data || null;
      if (
        typeof rawPreview === "string" &&
        rawPreview.startsWith("data:") &&
        !rawPreview.startsWith("data:image/")
      ) {
        try {
          const decodedPreview = atob(rawPreview.split(",")[1] || "");
          if (decodedPreview.startsWith("data:image/")) {
            rawPreview = decodedPreview;
          }
        } catch (decodeErr) {
          // Keep the original value if it is not double-encoded.
        }
      }
      return normalizeLegacyImageDataUrl(rawPreview);
    } catch (err) {
      return null;
    }
  }, [normalizeLegacyImageDataUrl]);

  const drawPersistedEntity = useCallback((ctx, entity) => {
    const from = entity?.from || { x: 0, y: 0 };
    const to = entity?.to || { x: 0, y: 0 };
    const points = Array.isArray(entity?.points) ? entity.points : [];
    const type = String(entity?.type || "").toLowerCase();

    const stroke =
      entity?.penColor || entity?.strokeColor || entity?.color || "#ff2e2e";
    const fillEnabled = Boolean(entity?.fillEnabled);
    const fillColor = entity?.fillColor || entity?.fill || "transparent";
    const fill = fillEnabled ? fillColor : "transparent";
    const lineWidth = Number(entity?.penSize || entity?.size || entity?.lineWidth || 2);
    const fontSize = Number(entity?.fontSize || 16);

    const left = Math.min(from.x || 0, to.x || 0);
    const top = Math.min(from.y || 0, to.y || 0);
    const width = Math.abs((to.x || 0) - (from.x || 0));
    const height = Math.abs((to.y || 0) - (from.y || 0));

    ctx.save();
    ctx.strokeStyle = stroke;
    ctx.fillStyle = fill;
    ctx.lineWidth = Number.isFinite(lineWidth) ? lineWidth : 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    if (type === "image") {
      ctx.restore();
      return;
    }

    switch (type) {
      case "line":
      case "arrow":
      case "cross":
      case "highlight":
      case "redact": {
        ctx.beginPath();
        ctx.moveTo(from.x || 0, from.y || 0);
        ctx.lineTo(to.x || 0, to.y || 0);
        ctx.stroke();
        break;
      }
      case "rectangle":
      case "rect": {
        if (fill !== "transparent") {
          ctx.fillRect(left, top, width, height);
        }
        ctx.strokeRect(left, top, width, height);
        break;
      }
      case "ellipse": {
        ctx.beginPath();
        ctx.ellipse(
          left + width / 2,
          top + height / 2,
          Math.max(1, width / 2),
          Math.max(1, height / 2),
          0,
          0,
          Math.PI * 2,
        );
        if (fill !== "transparent") {
          ctx.fill();
        }
        ctx.stroke();
        break;
      }
      case "polyline":
      case "freehand":
      case "cloud":
      case "triangle": {
        if (!points.length) {
          break;
        }
        ctx.beginPath();
        ctx.moveTo(points[0].x || 0, points[0].y || 0);
        for (let idx = 1; idx < points.length; idx += 1) {
          ctx.lineTo(points[idx].x || 0, points[idx].y || 0);
        }
        if (type === "triangle") {
          ctx.closePath();
          if (fill !== "transparent") {
            ctx.fill();
          }
        }
        ctx.stroke();
        break;
      }
      case "text":
      case "callout":
      case "rect-callout": {
        const text = String(entity?.text || "").trim();
        if (text) {
          ctx.fillStyle = stroke;
          ctx.font = `${Math.max(10, fontSize)}px Arial`;
          ctx.textBaseline = "top";
          ctx.fillText(text, from.x || left, from.y || top);
        }
        break;
      }
      default:
        break;
    }

    ctx.restore();
  }, []);

  const buildLegacyMarkupImage = useCallback(
    async (markup) => {
      const parsed = resolvePersistedMarkupData(markup);
      const baseScreenshot = normalizeLegacyImageDataUrl(
        parsed?.screenshotUrl ||
          parsed?.screenshotDataUrl ||
          parsed?.imageUrl ||
          markup?.imagePreview ||
          markup?.screenshotUrl ||
          markup?.imageUrl,
      );
      const overlayScreenshot = normalizeLegacyImageDataUrl(
        parsed?.drawOverlayDataUrl || markup?.drawOverlayDataUrl,
      );

      const loadImage = (source) =>
        new Promise((resolve, reject) => {
          const image = new Image();
          image.onload = () => resolve(image);
          image.onerror = () => reject(new Error("Failed to load image."));
          image.src = source;
        });

      const previewScreenshot = await fetchLegacyMarkupPreview(markup?.id);

      // Production displays the endpoint preview directly rather than
      // re-rendering it through canvas.
      if (previewScreenshot) {
        return previewScreenshot;
      }

      if (!baseScreenshot) {
        return overlayScreenshot || null;
      }

      try {
        const baseImage = await loadImage(baseScreenshot);
        const overlayImage = overlayScreenshot
          ? await loadImage(overlayScreenshot).catch(() => null)
          : null;
        const persistedEntities = Array.isArray(parsed?.entities)
          ? parsed.entities
          : [];

        if (!overlayImage && !persistedEntities.length) {
          return baseScreenshot;
        }

        const width = baseImage.naturalWidth || baseImage.width;
        const height = baseImage.naturalHeight || baseImage.height;
        if (!width || !height) {
          return baseScreenshot;
        }

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          return baseScreenshot;
        }

        ctx.drawImage(baseImage, 0, 0, width, height);
        if (overlayImage) {
          ctx.drawImage(overlayImage, 0, 0, width, height);
        }

        persistedEntities.forEach((entity) => {
          if (entity && typeof entity === "object") {
            drawPersistedEntity(ctx, entity);
          }
        });

        return canvas.toDataURL("image/png");
      } catch (err) {
        return baseScreenshot;
      }
    },
    [
      drawPersistedEntity,
      fetchLegacyMarkupPreview,
      normalizeLegacyImageDataUrl,
      resolvePersistedMarkupData,
    ],
  );

  const buildMarkupModalImage = useCallback(
    async (markup) => {
      const createdValue =
        markup?.created || markup?.createdDate || markup?.createdAt;
      const createdTimestamp = Date.parse(createdValue);
      const isLegacyMarkup =
        Number.isFinite(createdTimestamp) &&
        createdTimestamp < LEGACY_MARKUP_CUTOFF_TIMESTAMP;

      if (isLegacyMarkup) {
        return buildLegacyMarkupImage(markup);
      }

      const parsed = resolvePersistedMarkupData(markup);
      const screenshotUrl = normalizeImageSource(
        parsed?.screenshotUrl || markup?.screenshotUrl,
      );
      const overlayDataUrl = normalizeImageSource(
        parsed?.drawOverlayDataUrl || markup?.drawOverlayDataUrl,
      );
      const fallbackPreview = normalizeImageSource(markup?.imagePreview);

      if (!screenshotUrl) {
        return buildLegacyMarkupImage(markup);
      }

      const loadImage = (source) =>
        new Promise((resolve, reject) => {
          const image = new Image();
          image.onload = () => resolve(image);
          image.onerror = () => reject(new Error("Failed to load image."));
          image.src = source;
        });

      try {
        const baseImage = await loadImage(screenshotUrl);
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Number(baseImage.width || 0));
        canvas.height = Math.max(1, Number(baseImage.height || 0));

        const context = canvas.getContext("2d");
        if (!context) {
          return fallbackPreview;
        }

        context.clearRect(0, 0, canvas.width, canvas.height);
        context.drawImage(baseImage, 0, 0, canvas.width, canvas.height);

        if (overlayDataUrl) {
          try {
            const overlayImage = await loadImage(overlayDataUrl);
            context.drawImage(overlayImage, 0, 0, canvas.width, canvas.height);
          } catch (err) {
            // Keep base screenshot when overlay decode fails.
          }
        }

        const persistedEntities = Array.isArray(parsed?.entities)
          ? parsed.entities
          : Array.isArray(markup?.entities)
            ? markup.entities
            : [];

        if (persistedEntities.length) {
          persistedEntities.forEach((entity) => {
            if (!entity || typeof entity !== "object") {
              return;
            }
            drawPersistedEntity(context, entity);
          });
        }

        return canvas.toDataURL("image/png");
      } catch (err) {
        return fallbackPreview;
      }
    },
    [
      buildLegacyMarkupImage,
      drawPersistedEntity,
      normalizeImageSource,
      resolvePersistedMarkupData,
    ],
  );

  const fitGridColumns = useCallback(
    (apiInput) => {
      const api = apiInput || gridApi;
      if (!api) return;

      api.sizeColumnsToFit();
    },
    [gridApi],
  );

  const applyGridPageSizeMode = useCallback(
    (apiInput) => {
      const api = apiInput || gridApi;
      if (!api) return;

      const allRowCount = Math.max(rowData?.length || 0, 1);

      if (gridPageSizeMode === "all") {
        api.setGridOption("paginationAutoPageSize", false);
        api.setGridOption("paginationPageSize", allRowCount);
      } else if (gridPageSizeMode === "auto") {
        api.setGridOption("paginationAutoPageSize", true);
      } else {
        const pageSize = Number(gridPageSizeMode) || 25;
        api.setGridOption("paginationAutoPageSize", false);
        api.setGridOption("paginationPageSize", pageSize);
      }

      api.paginationGoToFirstPage();
    },
    [gridApi, gridPageSizeMode, rowData],
  );

  useEffect(() => {
    applyGridPageSizeMode();
    fitGridColumns();
  }, [applyGridPageSizeMode, fitGridColumns]);

  const gridReady = (params) => {
    setGridApi(params.api);
    requestAnimationFrame(() => {
      fitGridColumns(params.api);
      applyGridPageSizeMode(params.api);
    });
  };

  useEffect(() => {
    if (!gridApi) {
      return;
    }

    const frameId = requestAnimationFrame(() => {
      fitGridColumns(gridApi);
    });

    const timeoutId = setTimeout(() => {
      fitGridColumns(gridApi);
    }, 120);

    return () => {
      cancelAnimationFrame(frameId);
      clearTimeout(timeoutId);
    };
  }, [fitGridColumns, gridApi, rowData, gridPageSizeMode]);

  const colDefs = useMemo(
    () => [
      { field: "id", hide: true },
      {
        headerName: "Title",
        valueGetter: (p) => p.data?.title,
      },
      {
        headerName: "Category",
        valueGetter: (p) => p.data?.category,
      },
      {
        headerName: "Asset",
        valueGetter: (p) => p.data?.asset || p.data?.processArea,
      },
      {
        headerName: "Document Name",
        valueGetter: (p) => p.data?.documentName || "",
      },
      {
        headerName: "Created By",
        valueGetter: (p) => getMarkupCreatedBy(p.data),
      },
      {
        headerName: "Created On",
        valueGetter: (p) => formatDate(getMarkupCreatedOn(p.data)),
      },
      {
        headerName: "Assigned To",
        valueGetter: (p) => normalizeCreatedFor(p.data?.createdFor).join(", "),
      },
      {
        headerName: "Status",
        valueGetter: (p) => getMarkupGroupStatus(p.data),
        comparator: compareMarkupGroupStatus,
        rowGroup: true,
        hide: true,
        sort: "asc",
      },
    ],
    [
      compareMarkupGroupStatus,
      formatDate,
      getMarkupCreatedBy,
      getMarkupCreatedOn,
      getMarkupGroupStatus,
      normalizeCreatedFor,
    ],
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
    }),
    [],
  );

  const gridRef = useRef();

  const selectionChanged = () => {
    if (!gridApi) {
      return;
    }
    const selected = gridApi.getSelectedRows();
    setSelectedMarkup(selected?.[0] || null);
  };

  const autoGroupColumnDef = useMemo(() => {
    return {
      headerName: "Markup",
      valueGetter: (p) => p.data?.title || `Markup ${p.data?.id || ""}`,
      minWidth: 250,
      cellRenderer: "agGroupCellRenderer",
    };
  }, []);

  const openMarkupInfoModal = useCallback(async () => {
    const selectedRows =
      typeof gridApi?.getSelectedRows === "function"
        ? gridApi.getSelectedRows()
        : [];
    const activeSelection =
      normalizeMarkupRecord(selectedRows?.[0]) ||
      normalizeMarkupRecord(selectedMarkup || {});

    const markupId = Number(activeSelection?.id);
    if (!Number.isInteger(markupId) || markupId <= 0) {
      return;
    }

    const selectedRowMarkup = activeSelection;
    setInfoModal(true);
    setMarkupInfoLoading(true);
    setSelectedMarkupImage(null);
    setSelectedMarkupImageLoading(true);
    setSelectedMarkup(selectedRowMarkup);

    try {
      const detailsResponse = await axios({
        method: "get",
        url: `${HOST_URL}/markups/getMarkupById`,
        params: { markupId },
        withCredentials: true,
      });

      const detailsPayload =
        normalizeMarkupsPayload(detailsResponse)?.[0] ||
        decodePayloadCandidate(detailsResponse?.data?.data) ||
        decodePayloadCandidate(detailsResponse?.data) ||
        null;

      const effectiveMarkup = normalizeMarkupRecord({
        ...selectedRowMarkup,
        ...(detailsPayload || {}),
        id: markupId,
      });

      setSelectedMarkup(effectiveMarkup);

      const imageSource = await buildMarkupModalImage(effectiveMarkup);
      setSelectedMarkupImage(imageSource || null);
    } catch (err) {
      const imageSource = await buildMarkupModalImage(selectedRowMarkup);
      setSelectedMarkupImage(imageSource || null);
    } finally {
      setMarkupInfoLoading(false);
      setSelectedMarkupImageLoading(false);
    }
  }, [
    buildMarkupModalImage,
    decodePayloadCandidate,
    gridApi,
    normalizeMarkupRecord,
    normalizeMarkupsPayload,
    selectedMarkup,
  ]);

  const updateMarkupComments = useCallback(
    async (comments) => {
      const markupId = Number(selectedMarkup?.id);
      if (!Number.isInteger(markupId) || markupId <= 0) {
        return;
      }

      const updatedMarkup = {
        id: markupId,
        comments: comments || [],
      };

      setMarkupInfoSaving(true);
      try {
        const formData = new FormData();
        formData.append("formData", JSON.stringify(updatedMarkup));

        await axios({
          method: "post",
          url: HOST_URL + "/markups/updateMarkup",
          data: formData,
          headers: {
            "Content-Type": "multipart/form-data",
          },
          withCredentials: true,
        });

        setSelectedMarkup((prev) =>
          prev
            ? {
                ...prev,
                comments: comments || [],
              }
            : prev,
        );
        setRowData((prev) =>
          prev.map((item) =>
            Number(item?.id) === markupId
              ? { ...item, comments: comments || [] }
              : item,
          ),
        );
      } finally {
        setMarkupInfoSaving(false);
      }
    },
    [selectedMarkup],
  );

  return (
    <div className="project-config-page">
      <Container className="project-config-shell">
        <h1 className="project-config-title">Markup Information</h1>
        <Row>
          <Col
            className="project-config-actions"
            style={{
              display: "flex",
              justifyContent: "space-around",
              alignItems: "center",
              paddingTop: "8px",
              flexWrap: "wrap",
              gap: "0.5rem",
            }}
          >
            <Button
              className="project-config-action-btn mwh-btn-primary"
              variant="outline-info"
              size="sm"
              style={{ width: "150px" }}
              onClick={() => {
                openMarkupInfoModal();
              }}
              disabled={!selectedMarkup}
            >
              <span>View More Info</span>
            </Button>
          </Col>
        </Row>
        <Row className="project-config-grid-row">
          <div className="project-config-grid-wrap">
            <div className="project-config-grid-table">
              {markupsLoading ? (
                <LoadingScreen
                  input="Loading Markups"
                  width="12"
                  height="100%"
                  marginTop="6%"
                  variant="properties"
                  subtitle="Loading markup information..."
                  transparent={true}
                />
              ) : (
                <>
                  <AgGridReact
                    theme={agGridTheme}
                    rowData={rowData}
                    columnDefs={colDefs}
                    defaultColDef={defaultColDef}
                    ref={gridRef}
                    onSelectionChanged={selectionChanged}
                    groupDisplayType={"groupRows"}
                    autoGroupColumnDef={autoGroupColumnDef}
                    onGridReady={gridReady}
                    onGridSizeChanged={(params) => {
                      fitGridColumns(params.api);
                      if (gridPageSizeMode === "auto") {
                        applyGridPageSizeMode(params.api);
                      }
                    }}
                    onFirstDataRendered={(params) => {
                      fitGridColumns(params.api);
                      applyGridPageSizeMode(params.api);
                    }}
                    paginationAutoPageSize={gridPageSizeMode === "auto"}
                    paginationPageSizeSelector={false}
                    gridOptions={agGridOptionsSingle}
                  />
                  <div className="project-config-grid-rows-view">
                    <Form.Label className="mb-0 me-2">Page size</Form.Label>
                    <Form.Select
                      size="sm"
                      value={gridPageSizeMode}
                      onChange={(e) => {
                        setGridPageSizeMode(e.target.value);
                      }}
                      style={{ maxWidth: "180px" }}
                    >
                      <option value="auto">Auto</option>
                      <option value="25">25 per page</option>
                      <option value="50">50 per page</option>
                      <option value="100">100 per page</option>
                      <option value="all">All (scroll)</option>
                    </Form.Select>
                  </div>
                </>
              )}
            </div>
          </div>
        </Row>
      </Container>
      {/* MODALS */}
      <Modal
        show={infoModal}
        onHide={onCloseInfoModal}
        dialogClassName="project-info-modal markup-detail-modal"
        centered
      >
        <Modal.Header className="project-info-modal__header markup-detail-modal__header">
          <Modal.Title
            className="markup-detail-modal__title"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "10px",
              paddingRight: "52px",
              minWidth: 0,
            }}
          >
            <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
              {selectedMarkup?.title || `Markup ${selectedMarkup?.id || ""}`}
            </span>
            <span
              className="screenshot-markup__save-card-status-badge"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "4px 10px",
                borderRadius: "999px",
                fontSize: "11px",
                fontWeight: 700,
                letterSpacing: "0.02em",
                textTransform: "uppercase",
                whiteSpace: "nowrap",
                boxShadow: "0 6px 14px rgba(0, 0, 0, 0.18)",
                ...(getMarkupStatusDisplay(selectedMarkup || {}).badgeStyle || {}),
              }}
            >
              {getMarkupStatusDisplay(selectedMarkup || {}).label}
            </span>
          </Modal.Title>
          <div
            data-bs-theme="dark"
            style={{
              position: "absolute",
              right: 20,
            }}
          >
            <button
              type="button"
              className="btn-close project-info-modal__close"
              onClick={onCloseInfoModal}
              aria-label="Close"
            />
          </div>
        </Modal.Header>
        <Modal.Body className="project-info-modal__body markup-detail-modal__body">
          {markupInfoLoading ? (
            <div className="markup-detail-modal__loading">Loading markup details...</div>
          ) : (
            <div className="markup-detail-modal__layout">
              <div className="markup-detail-modal__meta-panel">
                <div className="markup-detail-modal__meta-grid">
                  <p className="markup-detail-modal__meta-label">Project</p>
                  <p className="markup-detail-modal__meta-value">
                    {selectedProject?.[0]?.text || "None"}
                  </p>

                  <p className="markup-detail-modal__meta-label">Category</p>
                  <p className="markup-detail-modal__meta-value">
                    {selectedMarkup?.category || "None"}
                  </p>

                  <p className="markup-detail-modal__meta-label">Asset</p>
                  <p className="markup-detail-modal__meta-value">
                    {selectedMarkup?.asset || selectedMarkup?.processArea || "None"}
                  </p>

                  <p className="markup-detail-modal__meta-label">Created by</p>
                  <p className="markup-detail-modal__meta-value">
                    {getMarkupCreatedBy(selectedMarkup) || "None"}
                  </p>

                  <p className="markup-detail-modal__meta-label">Created date</p>
                  <p className="markup-detail-modal__meta-value">
                    {formatDate(getMarkupCreatedOn(selectedMarkup)) || "None"}
                  </p>

                  <p className="markup-detail-modal__meta-label">Status</p>
                  <p className="markup-detail-modal__meta-value">
                    {getMarkupStatus(selectedMarkup)}
                  </p>

                  <p className="markup-detail-modal__meta-label">Assigned to</p>
                  <div className="markup-detail-modal__assigned-list">
                    {normalizeCreatedFor(selectedMarkup?.createdFor).length > 0 ? (
                      normalizeCreatedFor(selectedMarkup?.createdFor).map((userEmail) => (
                        <p key={userEmail} className="markup-detail-modal__meta-value">
                          {userEmail}
                        </p>
                      ))
                    ) : (
                      <p className="markup-detail-modal__meta-value">None</p>
                    )}
                  </div>

                </div>

                <p className="markup-detail-modal__comments-label">Comments</p>
                <div className="markup-detail-modal__comments">
                  <Comments
                    currentUser={userData?.emailId || userDetails?.email || ""}
                    comments={selectedMarkup?.comments || []}
                    updateMarkup={updateMarkupComments}
                  />
                </div>
              </div>

              <div className="markup-detail-modal__preview-panel">
                {selectedMarkupImageLoading ? (
                  <div className="markup-detail-modal__preview-empty">
                    Loading image...
                  </div>
                ) : selectedMarkupImage ? (
                  <img
                    className="markup-detail-modal__preview-image"
                    src={selectedMarkupImage}
                    alt={selectedMarkup?.title || "Markup preview"}
                    style={{
                      width: "100%",
                      maxHeight: "62vh",
                      objectFit: "contain",
                      borderRadius: "8px",
                    }}
                  />
                ) : (
                  <div className="markup-detail-modal__preview-empty">
                    No preview available
                  </div>
                )}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="markup-detail-modal__footer">
          <div className="markup-detail-modal__footer-actions screenshot-markup__save-card-actions">
            <button
              type="button"
              className="btn btn-sm btn-outline-danger screenshot-markup__load-btn screenshot-markup__delete-btn"
              onClick={onCloseInfoModal}
              disabled={markupInfoSaving}
            >
              Close
            </button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MarkupsInfo;
